import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@rocco/components/popover';
import { useTracker } from '@archipro-website/tracker';
import { Button } from '@rocco/components/button';
import { RadioGroup, RadioGroupItem } from '@rocco/components/radio-group';
import { ReactComponent as ArrowChevronDownIcon } from '@rocco/icons/svg/arrow/arrow-chevron-down.svg';
import type { SearchSortOptionResult } from '../../hook/use-search-sort-options';

/**
 * Dropdown for displaying and selecting sorting options.
 */
const SearchSort = ({
    optionResult,
    preventScrollReset = false,
}: {
    optionResult: SearchSortOptionResult;
    /**
     * Keep the current scroll position on sort (professional detail tabs).
     * Category/rooms directories keep the default scroll-to-top.
     */
    preventScrollReset?: boolean;
}) => {
    const { items, value, defaultSortName } = optionResult;
    const navigate = useNavigate();
    const tracker = useTracker();
    const [isOpen, setIsOpen] = useState(false);
    const isDefaultSortSelected = value?.header === defaultSortName;

    const handleSortChange = (value: string) => {
        // BC-1204 SortClick event
        tracker.log('SortClick', {
            url: new URL(window.location.href),
            data: {
                ExtraData: {
                    sortBy: value,
                },
            },
        });
        setIsOpen(false);
        navigate(items.find((item) => item.header === value)?.to ?? '', {
            preventScrollReset,
        });
    };

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
                <Button color="transparent" size="compact" weight="normal">
                    <span>
                        {isDefaultSortSelected ? 'Sort By' : value?.header}
                    </span>
                    <ArrowChevronDownIcon className="ml-2 size-4.5 transition-transform group-data-[state=open]:transform-[rotateX(180deg)]" />
                </Button>
            </PopoverTrigger>
            <PopoverContent align="end" paddings="small">
                <RadioGroup
                    className="gap-0"
                    value={value?.header}
                    defaultValue={defaultSortName}
                    onValueChange={handleSortChange}
                >
                    {items.map((item) => (
                        <label
                            key={item.header}
                            className="flex cursor-pointer items-center space-x-3 rounded-2 px-2 py-2.5 hover:bg-gray-50"
                        >
                            <RadioGroupItem value={item.header} size="small" />
                            <span className="text-base">{item.header}</span>
                        </label>
                    ))}
                </RadioGroup>
            </PopoverContent>
        </Popover>
    );
};

export default SearchSort;
